import { useEffect } from "react"
import { createPortal } from "react-dom"
import styled from "styled-components"

import { useLazyGetMessageTemplateQuery } from "store/api/contractorsApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setSelectedMessageTemplateID } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"

import decodeToMarkDown from "utils/markdown/decodeToMarkDown"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"
import Table from "components/table/Table"

const TableContainer = styled.div`
  padding: 15px;
`

const formatRepeatPeriod = (repeatPeriod: ApiTypes.Model.MessageTemplateFrequency | undefined): string => {
  if (!repeatPeriod) return ""

  if (repeatPeriod === "never") {
    return "Does not repeat"
  }

  return repeatPeriod.replace(/_/g, " ")
}

// TABLE HEADER
const headerCells: UITypes.TableHeaderCell[] = [
  {
    title: "ID",
    size: "tiny" as UITypes.TableCellSize,
    name: "id",
  },
  {
    title: "Title",
    size: "small" as UITypes.TableCellSize,
    name: "title",
  },
  {
    title: "Message",
    size: "middle" as UITypes.TableCellSize,
    name: "message",
    cellComponent: "markdown",
  },
  {
    title: "Repeat Period",
    size: "small" as UITypes.TableCellSize,
    name: "repeat_period",
  },
]

interface Props {
  onCancel: () => void
}

export const SeeMessageModal = ({ onCancel }: Props) => {
  const dispatch = useAppDispatch()
  const templateID = useAppSelector(state => state.tempData.selectedMessageTemplateID)
  const [triggerGetMessageTemplate, { data: templateData, isFetching }] = useLazyGetMessageTemplateQuery()
  const messageTemplate = templateData?.data

  useEffect(() => {
    if (templateID) {
      triggerGetMessageTemplate(templateID)
    }
  }, [templateID, triggerGetMessageTemplate])

  const handleCancel = () => {
    dispatch(setSelectedMessageTemplateID(undefined))
    onCancel()
  }

  return (
    <>
      {createPortal(
        <ModalLayout onClose={handleCancel} isDrawer isWithoutFooter isFetching={isFetching || !messageTemplate}>
          <ModalHeader title="Scheduled Message" borderless />
          <TableContainer>
            <Table
              headerCells={headerCells}
              isLoading={isFetching || !messageTemplate}
              fitContainer
              data={[
                {
                  id: { value: messageTemplate?.id },
                  title: { value: messageTemplate?.title },
                  message: {
                    value: messageTemplate?.content_rich ? decodeToMarkDown(messageTemplate?.content_rich) : "",
                  },
                  repeat_period: { value: formatRepeatPeriod(messageTemplate?.repeat_period) },
                  rowSettings: { id: messageTemplate?.id || 0 },
                },
              ]}
            />
          </TableContainer>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
